import { ColGroupDef } from 'ag-grid-enterprise';
import { format } from 'date-fns';
import { softwareVersionToString } from 'Mri.Web.Common';
import * as React from 'react';
import { ActiveDisplay } from '../../ActiveDisplay';
import {
    BooleanMeasurementField,
    getBooleanMeasurementValue,
    getMeasurementValue,
    getVideoInputOutputMeasurementValue,
    getVideoRXTXMeasurementValue,
    getWeatherCodeMeasurementValue,
    MeasurementField,
    MeasurementSourceSpanField,
    VideoInputOutputField,
    VideoRXTXField,
    WeatherCodeMeasurementField
} from './MeasurementFieldWrappers';
import { IDisplayFieldProps } from "./DisplayFields";
import {AccountStore} from "../../../Account/AccountStore";

export function getDisplayTelemetryFields(accountStore: AccountStore): ColGroupDef<ActiveDisplay>[]
{
    return [
        {
            headerName: 'Time/Timezone',
            children: [
                {
                    field: "currentTime",
                    cellRenderer: (props: IDisplayFieldProps) => {
                        const currentTime = props.data?.currentTime;

                        return (
                            <div>
                                {currentTime ? format(currentTime, 'EEEE, MMMM do yyyy, h:mm:ss a') : '-'}
                            </div>
                        );
                    },
                    initialHide: true,
                    sortable: false
                },
                {
                    field: "timezone",
                    cellRenderer: (props: IDisplayFieldProps) => {
                        const timezone = props.data?.timezone;

                        return (
                            <div>
                                {timezone ? timezone?.split('_').join(' ') : '-'}
                            </div>
                        );
                    },
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GeoVuSunrise.toString() as any,
                    headerName: "GeoVu Sunrise",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GeoVuSunrise} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GeoVuSunrise)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GeoVuSunset.toString() as any,
                    headerName: "GeoVu Sunset",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GeoVuSunset} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GeoVuSunset)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WxSunrise.toString() as any,
                    headerName: "Wx Sunrise",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.WxSunrise} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WxSunrise)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WxSunset.toString() as any,
                    headerName: "Wx Sunset",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.WxSunset} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WxSunset)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WxUtcOffset.toString() as any,
                    headerName: "Wx Utc Offset",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.WxUtcOffset} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WxUtcOffset)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sunrise.toString() as any,
                    headerName: "Sunrise",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sunrise} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sunrise)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sunset.toString() as any,
                    headerName: "Sunset",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sunset} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sunset)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.LocalTime.toString() as any,
                    headerName: "Local Time",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.LocalTime} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.LocalTime)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Software',
            children: [
                {
                    field: 'displaySoftware.mriSoftwareVersion',
                    headerName: "Mri Software",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data ? softwareVersionToString(props.data.displaySoftware?.mriSoftwareVersion) : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(props.data?.displaySoftware?.mriSoftwareVersion, true),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.bootLoaderSoftwareVersion',
                    headerName: "UBL",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data ? softwareVersionToString(props.data.displaySoftware?.bootLoaderSoftwareVersion) : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(props.data?.displaySoftware?.bootLoaderSoftwareVersion, true),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.bootSoftwareVersion',
                    headerName: "UBOOT",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data ? softwareVersionToString(props.data.displaySoftware?.bootSoftwareVersion) : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(props.data?.displaySoftware?.bootSoftwareVersion, true),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.kernelSoftwareVersion',
                    headerName: "Kernel",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data ? softwareVersionToString(props.data.displaySoftware?.kernelSoftwareVersion) : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(props.data?.displaySoftware?.kernelSoftwareVersion, true),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.rootSoftwareVersion',
                    headerName: "Root",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data ? softwareVersionToString(props.data.displaySoftware?.rootSoftwareVersion) : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(props.data?.displaySoftware?.rootSoftwareVersion, true),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.tileDriverVersions',
                    headerName: "Tile Driver 1",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 0 ? props.data.displaySoftware?.tileDriverVersions[0] : undefined))
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>props.data ?
                        softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 0 ? props.data?.displaySoftware?.tileDriverVersions[0] : undefined), true) : '-',
                    sortable: false,
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.tileDriverVersions',
                    headerName: "Tile Driver 2",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 1 ? props.data.displaySoftware?.tileDriverVersions[1] : undefined))
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) => props.data ?
                        softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 1 ? props.data?.displaySoftware?.tileDriverVersions[1] : undefined), true) : '-',
                    sortable: false,
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.tileDriverVersions',
                    headerName: "Tile Driver 3",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 2 ? props.data.displaySoftware?.tileDriverVersions[2] : undefined))
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) => props.data ?
                        softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 2 ? props.data?.displaySoftware?.tileDriverVersions[2] : undefined), true) : '-',
                    sortable: false,
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.tileDriverVersions',
                    headerName: "Tile Driver 4",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 3 ? props.data.displaySoftware?.tileDriverVersions[3] : undefined))
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) => props.data ?
                        softwareVersionToString((props.data.displaySoftware?.tileDriverVersions?.length > 3 ? props.data?.displaySoftware?.tileDriverVersions[3] : undefined), true) : '-',
                    sortable: false,
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.infiniteTouchConfigVersionPrimary',
                    headerName: "InfiniteTouch® Config Version (Primary)",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString(props.data.displaySoftware?.infiniteTouchConfigVersionPrimary)
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(
                            props.data?.displaySoftware?.infiniteTouchConfigVersionPrimary,
                            true
                        ),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.infiniteTouchFirmwareVersionPrimary',
                    headerName: "InfiniteTouch® Firmware Version (Primary)",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString(props.data.displaySoftware?.infiniteTouchFirmwareVersionPrimary)
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(
                            props.data?.displaySoftware?.infiniteTouchFirmwareVersionPrimary,
                            true
                        ),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.infiniteTouchConfigVersionSecondary',
                    headerName: "InfiniteTouch® Config Version (Secondary)",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString(props.data.displaySoftware?.infiniteTouchConfigVersionSecondary)
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(
                            props.data?.displaySoftware?.infiniteTouchConfigVersionSecondary,
                            true
                        ),
                    initialHide: true,
                },
                {
                    field: 'displaySoftware.infiniteTouchFirmwareVersionSecondary',
                    headerName: "InfiniteTouch® Firmware Version (Secondary)",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data
                                ? softwareVersionToString(props.data.displaySoftware?.infiniteTouchFirmwareVersionSecondary)
                                : '-'}
                        </div>
                    ),
                    valueGetter: (props) =>
                        softwareVersionToString(
                            props.data?.displaySoftware?.infiniteTouchFirmwareVersionSecondary,
                            true
                        ),
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'System Power',
            children: [
                {
                    field: 'powerGovSourceName',
                    headerName: "Power Governor Source Name",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <div>
                            {props.data?.powerGovSourceName ? (
                                <div>
                                    <span>{props.data.powerGovSourceName}</span>
                                    {(props.data.powerGovSourceName === 'Reduction System Warm' ||
                                        props.data.powerGovSourceName === 'Reduction Backlight Warm' ||
                                        props.data.powerGovSourceName === 'Reduction Closed Loop Warm') && (
                                        <MeasurementSourceSpanField
                                            prefixComma
                                            id={Mri.WebApi.MeasurementId.CoolVuZone1Source}
                                            {...props}
                                        />
                                    )}
                                </div>
                            ) : (
                                '-'
                            )}
                        </div>
                    ),
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.MaxPowerSupplyTemp.toString() as any,
                    headerName: "Max PS °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MaxPowerSupplyTemp} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MaxPowerSupplyTemp)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UnitTotalPower.toString() as any,
                    headerName: "Unit Total Power",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UnitTotalPower} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UnitTotalPower)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UnitTotalInletAmps.toString() as any,
                    headerName: "Unit Total Inlet Current",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UnitTotalInletAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UnitTotalInletAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UnitTotalV48Amps.toString() as any,
                    headerName: "Unit Total Current",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UnitTotalV48Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UnitTotalV48Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.SurgeSuppressorFailedStatus.toString() as any,
                    headerName: "Surge Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Fail"
                            falseValue="Ok"
                            id={Mri.WebApi.MeasurementId.SurgeSuppressorFailedStatus}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.SurgeSuppressorFailedStatus,
                                "Fail",
                                "Ok"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerGovernorSource.toString() as any,
                    headerName: "Power Governor Source",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerGovernorSource} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PowerGovernorSource)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.KilowattHour.toString() as any,
                    headerName: "Kw/H",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.KilowattHour} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.KilowattHour)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UnitKilowattHour.toString() as any,
                    headerName: "Unit Kw/H",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UnitKilowattHour} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UnitKilowattHour)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UnitPowerSupplyEfficiency.toString() as any,
                    headerName: "Unit PS Eff",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UnitPowerSupplyEfficiency} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.UnitPowerSupplyEfficiency
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InletUnitDailyMaxCurrent.toString() as any,
                    headerName: "Inlet Daily Max Current",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InletUnitDailyMaxCurrent} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.InletUnitDailyMaxCurrent
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InletUnitLifetimeMaxCurrent.toString() as any,
                    headerName: "Inlet Lifetime Max Current",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InletUnitLifetimeMaxCurrent} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.InletUnitLifetimeMaxCurrent
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AvgInletVolts.toString() as any,
                    headerName: "Avg Inlet Volts",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.AvgInletVolts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.AvgInletVolts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.TotalInletAmps.toString() as any,
                    headerName: "Total Inlet Current",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.TotalInletAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.TotalInletAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.TotalInletWatts.toString() as any,
                    headerName: "Total Inlet Watts",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.TotalInletWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.TotalInletWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu12VTotalAmps.toString() as any,
                    headerName: "PS 12v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu12VTotalAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu12VTotalAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu12VTotalWatts.toString() as any,
                    headerName: "PS 12v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu12VTotalWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu12VTotalWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28TotalAmps.toString() as any,
                    headerName: "PS 28v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28TotalAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28TotalAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28TotalWatts.toString() as any,
                    headerName: "PS 28v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28TotalWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28TotalWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48TotalAmps.toString() as any,
                    headerName: "PS Total A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48TotalAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48TotalAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48TotalWatts.toString() as any,
                    headerName: "PS Total W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48TotalWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48TotalWatts)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Door',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Side1MagneticSensorStatus.toString() as any,
                    headerName: "Side 1 Magnet Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.Side1MagneticSensorStatus}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Side1MagneticSensorStatus,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Side1Latch1Status.toString() as any,
                    headerName: "Side 1 Left Latch Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.Side1Latch1Status}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Side1Latch1Status,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Side1Latch2Status.toString() as any,
                    headerName: "Side 1 Right Latch Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.Side1Latch2Status}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Side1Latch2Status,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Side2MagneticSensorStatus.toString() as any,
                    headerName: "Side 2 Magnet Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.Side2MagneticSensorStatus}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Side2MagneticSensorStatus,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Side2Latch1Status.toString() as any,
                    headerName: "Side 2 Left Latch Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.Side2Latch1Status}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Side2Latch1Status,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Side2Latch2Status.toString() as any,
                    headerName: "Side 2 Right Latch Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.Side2Latch2Status}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Side2Latch2Status,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AnyDoorOpenStatus.toString() as any,
                    headerName: "Door",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <BooleanMeasurementField
                            trueValue="Open"
                            falseValue="Closed"
                            id={Mri.WebApi.MeasurementId.AnyDoorOpenStatus}
                            {...props}
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getBooleanMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.AnyDoorOpenStatus,
                                "Open",
                                "Closed"
                            )
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'PSU 1',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Inlet1Volts.toString() as any,
                    headerName: "Inlet 1 V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet1Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet1Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet1Amps.toString() as any,
                    headerName: "Inlet 1 C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet1Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet1Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet1Watts.toString() as any,
                    headerName: "Inlet 1 W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet1Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet1Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply1Efficiency.toString() as any,
                    headerName: "Efficiency",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply1Efficiency} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.PowerSupply1Efficiency
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply1PF.toString() as any,
                    headerName: "PF",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply1PF} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PowerSupply1PF)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PrimaryPsu1Vtrim.toString() as any,
                    headerName: "Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PrimaryPsu1Vtrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PrimaryPsu1Vtrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu1Volts.toString() as any,
                    headerName: "V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu1Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu1Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu1Amps.toString() as any,
                    headerName: "A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu1Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu1Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu1Watts.toString() as any,
                    headerName: "W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu1Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu1Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu1Celsius.toString() as any,
                    headerName: "°C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu1Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu1Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu1_12VVolts.toString() as any,
                    headerName: "12v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu1_12VVolts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu1_12VVolts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu1_12VAmps.toString() as any,
                    headerName: "12v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu1_12VAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu1_12VAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu1_12VWatts.toString() as any,
                    headerName: "12v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu1_12VWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu1_12VWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V12VPsu1VTrim.toString() as any,
                    headerName: "12v Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V12VPsu1VTrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V12VPsu1VTrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu1Volts.toString() as any,
                    headerName: "28v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu1Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu1Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu1Amps.toString() as any,
                    headerName: "28v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu1Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu1Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu1Watts.toString() as any,
                    headerName: "28v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu1Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu1Watts)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'PSU 2',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Inlet2Volts.toString() as any,
                    headerName: "Inlet 2 V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet2Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet2Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet2Amps.toString() as any,
                    headerName: "Inlet 2 C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet2Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet2Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet2Watts.toString() as any,
                    headerName: "Inlet 2 W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet2Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet2Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply2Efficiency.toString() as any,
                    headerName: "Efficiency",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply2Efficiency} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.PowerSupply2Efficiency
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply2PF.toString() as any,
                    headerName: "PF",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply2PF} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PowerSupply2PF)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PrimaryPsu2Vtrim.toString() as any,
                    headerName: "Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PrimaryPsu2Vtrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PrimaryPsu2Vtrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu2Volts.toString() as any,
                    headerName: "V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu2Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu2Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu2Amps.toString() as any,
                    headerName: "A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu2Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu2Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu2Watts.toString() as any,
                    headerName: "W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu2Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu2Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu2Celsius.toString() as any,
                    headerName: "°C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu2Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu2Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu2_12VVolts.toString() as any,
                    headerName: "12v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu2_12VVolts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu2_12VVolts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu2_12VAmps.toString() as any,
                    headerName: "12v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu2_12VAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu2_12VAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu2_12VWatts.toString() as any,
                    headerName: "12v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu2_12VWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu2_12VWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V12VPsu2VTrim.toString() as any,
                    headerName: "12v Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V12VPsu2VTrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V12VPsu2VTrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu2Volts.toString() as any,
                    headerName: "28v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu2Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu2Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu2Amps.toString() as any,
                    headerName: "28v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu2Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu2Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu2Watts.toString() as any,
                    headerName: "28v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu2Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu2Watts)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'PSU 3',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Inlet3Volts.toString() as any,
                    headerName: "Inlet 3 V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet3Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet3Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet3Amps.toString() as any,
                    headerName: "Inlet 3 C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet3Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet3Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet3Watts.toString() as any,
                    headerName: "Inlet 3 W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet3Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet3Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply3Efficiency.toString() as any,
                    headerName: "Efficiency",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply3Efficiency} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.PowerSupply3Efficiency
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply3PF.toString() as any,
                    headerName: "PF",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply3PF} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PowerSupply3PF)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PrimaryPsu3Vtrim.toString() as any,
                    headerName: "Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PrimaryPsu3Vtrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PrimaryPsu3Vtrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu3Volts.toString() as any,
                    headerName: "V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu3Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu3Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu3Amps.toString() as any,
                    headerName: "A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu3Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu3Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu3Watts.toString() as any,
                    headerName: "W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu3Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu3Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu3Celsius.toString() as any,
                    headerName: "°C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu3Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu3Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu3_12VVolts.toString() as any,
                    headerName: "12v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu3_12VVolts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu3_12VVolts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu3_12VAmps.toString() as any,
                    headerName: "12v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu3_12VAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu3_12VAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu3_12VWatts.toString() as any,
                    headerName: "12v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu3_12VWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu3_12VWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V12VPsu3VTrim.toString() as any,
                    headerName: "12v Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V12VPsu3VTrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V12VPsu3VTrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu3Volts.toString() as any,
                    headerName: "28v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu3Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu3Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu3Amps.toString() as any,
                    headerName: "28v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu3Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu3Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu3Watts.toString() as any,
                    headerName: "28v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu3Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu3Watts)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'PSU 4',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Inlet4Volts.toString() as any,
                    headerName: "Inlet 4 V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet4Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet4Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet4Amps.toString() as any,
                    headerName: "Inlet 4 C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet4Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet4Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Inlet4Watts.toString() as any,
                    headerName: "Inlet 4 W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Inlet4Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Inlet4Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply4Efficiency.toString() as any,
                    headerName: "Efficiency",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply4Efficiency} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.PowerSupply4Efficiency
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PowerSupply4PF.toString() as any,
                    headerName: "PF",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerSupply4PF} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PowerSupply4PF)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PrimaryPsu4Vtrim.toString() as any,
                    headerName: "Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PrimaryPsu4Vtrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PrimaryPsu4Vtrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu4Volts.toString() as any,
                    headerName: "V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu4Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu4Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu4Amps.toString() as any,
                    headerName: "A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu4Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu4Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu4Watts.toString() as any,
                    headerName: "W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu4Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu4Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V48Psu4Celsius.toString() as any,
                    headerName: "°C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V48Psu4Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V48Psu4Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu4_12VVolts.toString() as any,
                    headerName: "12v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu4_12VVolts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu4_12VVolts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu4_12VAmps.toString() as any,
                    headerName: "12v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu4_12VAmps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu4_12VAmps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Psu4_12VWatts.toString() as any,
                    headerName: "12v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Psu4_12VWatts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Psu4_12VWatts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V12VPsu4VTrim.toString() as any,
                    headerName: "12v Vtrim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V12VPsu4VTrim} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V12VPsu4VTrim)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu4Volts.toString() as any,
                    headerName: "28v V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu4Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu4Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu4Amps.toString() as any,
                    headerName: "28v A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu4Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu4Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.V28Psu4Watts.toString() as any,
                    headerName: "28v W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.V28Psu4Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.V28Psu4Watts)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Custom PSU',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.CustomAuxPsu1Volts.toString() as any,
                    headerName: "PS1 V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CustomAuxPsu1Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CustomAuxPsu1Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CustomAuxPsu1Amps.toString() as any,
                    headerName: "PS1 A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CustomAuxPsu1Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CustomAuxPsu1Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CustomAuxPsu1Watts.toString() as any,
                    headerName: "PS1 W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CustomAuxPsu1Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CustomAuxPsu1Watts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CustomAuxPsu2Volts.toString() as any,
                    headerName: "PS2 V",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CustomAuxPsu2Volts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CustomAuxPsu2Volts)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CustomAuxPsu2Amps.toString() as any,
                    headerName: "PS2 A",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CustomAuxPsu2Amps} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CustomAuxPsu2Amps)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CustomAuxPsu2Watts.toString() as any,
                    headerName: "PS2 W",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CustomAuxPsu2Watts} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CustomAuxPsu2Watts)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Fans',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.ClosedLoopPwmLevel.toString() as any,
                    headerName: "Closed PWM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.ClosedLoopPwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.ClosedLoopPwmLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.OpenLoopPwmLevel.toString() as any,
                    headerName: "Open PWM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.OpenLoopPwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.OpenLoopPwmLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HeatExchangePwmLevel.toString() as any,
                    headerName: "Heat Exch PWM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HeatExchangePwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.HeatExchangePwmLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.FanBank1PwmLevel.toString() as any,
                    headerName: "Fan1 %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.FanBank1PwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.FanBank1PwmLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.FanBank2PwmLevel.toString() as any,
                    headerName: "Fan2 %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.FanBank2PwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.FanBank2PwmLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.FanBank3PwmLevel.toString() as any,
                    headerName: "Fan3 %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.FanBank3PwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.FanBank3PwmLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan1RPM.toString() as any,
                    headerName: "Fan1 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan1RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan1RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan2RPM.toString() as any,
                    headerName: "Fan2 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan2RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan2RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan3RPM.toString() as any,
                    headerName: "Fan3 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan3RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan3RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan4RPM.toString() as any,
                    headerName: "Fan4 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan4RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan4RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan5RPM.toString() as any,
                    headerName: "Fan5 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan5RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan5RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan6RPM.toString() as any,
                    headerName: "Fan6 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan6RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan6RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan7RPM.toString() as any,
                    headerName: "Fan7 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan7RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan7RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan8RPM.toString() as any,
                    headerName: "Fan8 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan8RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan8RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan9RPM.toString() as any,
                    headerName: "Fan9 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan9RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan9RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan10RPM.toString() as any,
                    headerName: "Fan10 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan10RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan10RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan11RPM.toString() as any,
                    headerName: "Fan11 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan11RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan11RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan12RPM.toString() as any,
                    headerName: "Fan12 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan12RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan12RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan13RPM.toString() as any,
                    headerName: "Fan13 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan13RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan13RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan14RPM.toString() as any,
                    headerName: "Fan14 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan14RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan14RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan15RPM.toString() as any,
                    headerName: "Fan15 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan15RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan15RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan16RPM.toString() as any,
                    headerName: "Fan16 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan16RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan16RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan17RPM.toString() as any,
                    headerName: "Fan17 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan17RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan17RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan18RPM.toString() as any,
                    headerName: "Fan18 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan18RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan18RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan19RPM.toString() as any,
                    headerName: "Fan19 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan19RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan19RPM)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Fan20RPM.toString() as any,
                    headerName: "Fan20 RPM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Fan20RPM} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Fan20RPM)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Backlight',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.PowerGovernorLimit.toString() as any,
                    headerName: "Power Limit",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PowerGovernorLimit} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PowerGovernorLimit)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1Brightness.toString() as any,
                    headerName: "BL1 Nits",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1Brightness} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight1Brightness)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1DesiredBrightness.toString() as any,
                    headerName: "BL1 Desired Nits",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1DesiredBrightness} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight1DesiredBrightness
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AvgBacklight1LightSensor.toString() as any,
                    headerName: "BL1 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.AvgBacklight1LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.AvgBacklight1LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1DacLevel.toString() as any,
                    headerName: "BL1 DAC",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1DacLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight1DacLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1PwmLevel.toString() as any,
                    headerName: "BL1 PWM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1PwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight1PwmLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AmbientLight1.toString() as any,
                    headerName: "Amb1 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.AmbientLight1} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.AmbientLight1)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1BlockDimmingState.toString() as any,
                    headerName: "BL1 Dim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight1BlockDimmingState
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgLevel.toString() as any,
                    headerName: "BL1 Dim Savings",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgMultiframeLevel.toString() as any,
                    headerName: "BL1 Multiframe Dim Savings",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField
                            id={Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgMultiframeLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight1BlockDimmingAvgMultiframeLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2Brightness.toString() as any,
                    headerName: "BL2 Nits",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2Brightness} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight2Brightness)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2DesiredBrightness.toString() as any,
                    headerName: "BL2 Desired Nits",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2DesiredBrightness} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight2DesiredBrightness
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AvgBacklight2LightSensor.toString() as any,
                    headerName: "BL2 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.AvgBacklight2LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.AvgBacklight2LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2DacLevel.toString() as any,
                    headerName: "BL2 DAC",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2DacLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight2DacLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2PwmLevel.toString() as any,
                    headerName: "BL2 PWM",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2PwmLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight2PwmLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AmbientLight2.toString() as any,
                    headerName: "Amb2 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.AmbientLight2} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.AmbientLight2)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2BlockDimmingState.toString() as any,
                    headerName: "BL2 Dim",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight2BlockDimmingState
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgLevel.toString() as any,
                    headerName: "BL2 Dim Savings",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgMultiframeLevel.toString() as any,
                    headerName: "BL2 Multiframe Dim Savings",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField
                            id={Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgMultiframeLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(
                                props.data.measurements,
                                Mri.WebApi.MeasurementId.Backlight2BlockDimmingAvgMultiframeLevel
                            )
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile1SensorCelsius.toString() as any,
                    headerName: "Tile1 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile1SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile1SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile1LightSensor.toString() as any,
                    headerName: "Tile1 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile1LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile1LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile2SensorCelsius.toString() as any,
                    headerName: "Tile2 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile2SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile2SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile2LightSensor.toString() as any,
                    headerName: "Tile2 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile2LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile2LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile3SensorCelsius.toString() as any,
                    headerName: "Tile3 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile3SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile3SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile3LightSensor.toString() as any,
                    headerName: "Tile3 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile3LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile3LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile4SensorCelsius.toString() as any,
                    headerName: "Tile4 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile4SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile4SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile4LightSensor.toString() as any,
                    headerName: "Tile4 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile4LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile4LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile5SensorCelsius.toString() as any,
                    headerName: "Tile5 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile5SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile5SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile5LightSensor.toString() as any,
                    headerName: "Tile5 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile5LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile5LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile6SensorCelsius.toString() as any,
                    headerName: "Tile6 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile6SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile6SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile6LightSensor.toString() as any,
                    headerName: "Tile6 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile6LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile6LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile7SensorCelsius.toString() as any,
                    headerName: "Tile7 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile7SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile7SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile7LightSensor.toString() as any,
                    headerName: "Tile7 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile7LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile7LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile8SensorCelsius.toString() as any,
                    headerName: "Tile8 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile8SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile8SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile8LightSensor.toString() as any,
                    headerName: "Tile8 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile8LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile8LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile9SensorCelsius.toString() as any,
                    headerName: "Tile9 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile9SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile9SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile9LightSensor.toString() as any,
                    headerName: "Tile9 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile9LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile9LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile10SensorCelsius.toString() as any,
                    headerName: "Tile10 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile10SensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile10SensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Tile10LightSensor.toString() as any,
                    headerName: "Tile10 Light",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Tile10LightSensor} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Tile10LightSensor)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.StaticSideBacklightStatus.toString() as any,
                    headerName: "Static BL Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.StaticSideBacklightStatus} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.StaticSideBacklightStatus)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'WeatherVu',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.CloudCoverRatio.toString() as any,
                    headerName: "Cloud Cover %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CloudCoverRatio} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CloudCoverRatio)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HumidityLevel.toString() as any,
                    headerName: "Relative Humidity",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HumidityLevel} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HumidityLevel)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WeatherCode.toString() as any,
                    headerName: "Weather",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <WeatherCodeMeasurementField id={Mri.WebApi.MeasurementId.WeatherCode} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getWeatherCodeMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WeatherCode)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.BarometricPressure.toString() as any,
                    headerName: "Barometric Pressure",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.BarometricPressure} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.BarometricPressure)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WindSpeed.toString() as any,
                    headerName: "Wind Speed",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.WindSpeed} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WindSpeed)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WindDirection.toString() as any,
                    headerName: "Wind Direction",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.WindDirection} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WindDirection)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.WindGust.toString() as any,
                    headerName: "Wind Gust",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.WindGust} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.WindGust)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.LocalHumidity.toString() as any,
                    headerName: "Local Humidity",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.LocalHumidity} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.LocalHumidity)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'CoolVu',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.CoolVuZone1Source.toString() as any,
                    headerName: "Zone 1 Source",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CoolVuZone1Source} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CoolVuZone1Source)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CoolVuZone1Celsius.toString() as any,
                    headerName: "Zone 1 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CoolVuZone1Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CoolVuZone1Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CoolVuZone2Source.toString() as any,
                    headerName: "Zone 2 Source",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CoolVuZone2Source} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CoolVuZone2Source)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CoolVuZone2Celsius.toString() as any,
                    headerName: "Zone 2 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CoolVuZone2Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CoolVuZone2Celsius)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Environmentals',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Backlight1Celsius.toString() as any,
                    headerName: "Backlight1 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight1Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight1MaxCelsius.toString() as any,
                    headerName: "Backlight1 Max °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight1MaxCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight1MaxCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2Celsius.toString() as any,
                    headerName: "Backlight2 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2Celsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight2Celsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Backlight2MaxCelsius.toString() as any,
                    headerName: "Backlight2 Max °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Backlight2MaxCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Backlight2MaxCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.ClosedLoopCelsius.toString() as any,
                    headerName: "Closed Loop °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.ClosedLoopCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.ClosedLoopCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.ClosedLoopCelsius2.toString() as any,
                    headerName: "Closed Loop2 °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.ClosedLoopCelsius2} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.ClosedLoopCelsius2)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PressureDifferential.toString() as any,
                    headerName: "Pressure Differential",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PressureDifferential} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PressureDifferential)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.PressureSensorTemperature.toString() as any,
                    headerName: "Pressure °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PressureSensorTemperature} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PressureSensorTemperature)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.SealStatus.toString() as any,
                    headerName: "Seal Status",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.SealStatus} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.SealStatus)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.LeakDetected.toString() as any,
                    headerName: "Leak Detected",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.LeakDetected} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.LeakDetected)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.FpgaDieCelsius.toString() as any,
                    headerName: "Fpga Die °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.FpgaDieCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.FpgaDieCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.ProcessorBoardCelsius.toString() as any,
                    headerName: "Processor °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.ProcessorBoardCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.ProcessorBoardCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InletSensorCelsius.toString() as any,
                    headerName: "Inlet Sensor °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InletSensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.InletSensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.LocalWeatherCelsius.toString() as any,
                    headerName: "Local Weather °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.LocalWeatherCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.LocalWeatherCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.AmbientDeltaCelsius.toString() as any,
                    headerName: "Ambient Delta °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.AmbientDeltaCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.AmbientDeltaCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HumiditySensorCelsius.toString() as any,
                    headerName: "Humidity Sensor °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HumiditySensorCelsius} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HumiditySensorCelsius)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HumidityUpper.toString() as any,
                    headerName: "Humidity Upper",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HumidityUpper} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HumidityUpper)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HumidityLower.toString() as any,
                    headerName: "Humidity Lower",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HumidityLower} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HumidityLower)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UpperMetalTemp.toString() as any,
                    headerName: "Upper Metal °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UpperMetalTemp} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UpperMetalTemp)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.LowerMetalTemp.toString() as any,
                    headerName: "Lower Metal °C",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.LowerMetalTemp} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.LowerMetalTemp)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CalculatedDewpoint.toString() as any,
                    headerName: "Calculated Dewpoint",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CalculatedDewpoint} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CalculatedDewpoint)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.DewpointSpreadUpper.toString() as any,
                    headerName: "DPS Upper",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.DewpointSpreadUpper} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.DewpointSpreadUpper)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.DewpointSpreadLower.toString() as any,
                    headerName: "DPS Lower",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.DewpointSpreadLower} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.DewpointSpreadLower)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.DewpointSpreadUtilized.toString() as any,
                    headerName: "DPS Utilized",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.DewpointSpreadUtilized} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.DewpointSpreadUtilized)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.DewpointInhibitActive.toString() as any,
                    headerName: "Dewpoint Inhibit Active",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.DewpointInhibitActive} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.DewpointInhibitActive)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.DewpointSpread.toString() as any,
                    headerName: "Dewpoint Spread",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.DewpointSpread} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.DewpointSpread)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'GPS',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.PerCentTimeGpsValid.toString() as any,
                    headerName: "% Valid",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.PerCentTimeGpsValid} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.PerCentTimeGpsValid)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsHasFix.toString() as any,
                    headerName: "Fix",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GpsHasFix} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsHasFix)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsHDOP.toString() as any,
                    headerName: "HDOP",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GpsHDOP} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsHDOP)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsSatInView.toString() as any,
                    headerName: "# Sat",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GpsSatInView} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsSatInView)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsSatInFix.toString() as any,
                    headerName: "# Fix",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GpsSatInFix} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsSatInFix)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsLatitude.toString() as any,
                    headerName: "Latitude",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField
                            id={Mri.WebApi.MeasurementId.GpsLatitude}
                            ignoreZero
                            {...props}
                            fallbackValue={
                                props.data?.configPoll?.geoVuConfig?.latitude
                                    ? props.data?.configPoll?.geoVuConfig?.latitude + ' (Config)'
                                    : props.data?.unitLocation?.latitude + ' (SmartVu)'
                            }
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsLatitude)
                            : props.data?.configPoll?.geoVuConfig?.latitude
                                ? props.data?.configPoll?.geoVuConfig?.latitude
                                : props.data?.unitLocation?.latitude,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsLongitude.toString() as any,
                    headerName: "Longitude",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField
                            id={Mri.WebApi.MeasurementId.GpsLongitude}
                            ignoreZero
                            {...props}
                            fallbackValue={
                                props.data?.configPoll?.geoVuConfig?.longitude
                                    ? props.data?.configPoll?.geoVuConfig?.longitude + ' (Config)'
                                    : props.data?.unitLocation?.longitude + ' (SmartVu)'
                            }
                        />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsLongitude)
                            : props.data?.configPoll?.geoVuConfig?.longitude
                                ? props.data?.configPoll?.geoVuConfig?.longitude
                                : props.data?.unitLocation?.longitude,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsSignalStrength.toString() as any,
                    headerName: "Signal Strength",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GpsSignalStrength} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsSignalStrength)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.GpsHasValidSerialData.toString() as any,
                    headerName: "Valid",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.GpsHasValidSerialData} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.GpsHasValidSerialData)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'InfiniteTouch',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Sigma1CDAC.toString() as any,
                    headerName: "1 CDAC",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1CDAC} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1CDAC)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InfiniteTouch1Celsius1.toString() as any,
                    headerName: "1 °C1",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InfiniteTouch1Celsius1} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.InfiniteTouch1Celsius1)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InfiniteTouch1Celsius2.toString() as any,
                    headerName: "1 °C2",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InfiniteTouch1Celsius2} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.InfiniteTouch1Celsius2)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1FrameCount.toString() as any,
                    headerName: "1 Frame Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1FrameCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1FrameCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1FrameRate.toString() as any,
                    headerName: "1 Frame Rate",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1FrameRate} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1FrameRate)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1MitigatingResetCount.toString() as any,
                    headerName: "1 Miti Reset",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1MitigatingResetCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1MitigatingResetCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1NoisyFrequencyCount.toString() as any,
                    headerName: "1 Noisy Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1NoisyFrequencyCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1NoisyFrequencyCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1RailedCount.toString() as any,
                    headerName: "1 Railed Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1RailedCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1RailedCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1RebaselineCount.toString() as any,
                    headerName: "1 Rebase Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1RebaselineCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1RebaselineCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1SelfAmplitude.toString() as any,
                    headerName: "1 Self Amp",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1SelfAmplitude} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1SelfAmplitude)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1TouchCount.toString() as any,
                    headerName: "1 Touch Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1TouchCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1TouchCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1TouchId.toString() as any,
                    headerName: "1 Touch Id",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1TouchId} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1TouchId)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma1Uptime.toString() as any,
                    headerName: "1 Uptime",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma1Uptime} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma1Uptime)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2CDAC.toString() as any,
                    headerName: "2 CDAC",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2CDAC} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2CDAC)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InfiniteTouch2Celsius1.toString() as any,
                    headerName: "2 °C1",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InfiniteTouch2Celsius1} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.InfiniteTouch2Celsius1)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.InfiniteTouch2Celsius2.toString() as any,
                    headerName: "2 °C2",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.InfiniteTouch2Celsius2} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.InfiniteTouch2Celsius2)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2FrameCount.toString() as any,
                    headerName: "2 Frame Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2FrameCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2FrameCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2FrameRate.toString() as any,
                    headerName: "2 Frame Rate",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2FrameRate} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2FrameRate)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2MitigatingResetCount.toString() as any,
                    headerName: "2 Miti Reset",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2MitigatingResetCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2MitigatingResetCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2NoisyFrequencyCount.toString() as any,
                    headerName: "2 Noisy Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2NoisyFrequencyCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2NoisyFrequencyCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2RailedCount.toString() as any,
                    headerName: "2 Railed Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2RailedCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2RailedCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2RebaselineCount.toString() as any,
                    headerName: "2 Rebase Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2RebaselineCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2RebaselineCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2SelfAmplitude.toString() as any,
                    headerName: "2 Self Amp",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2SelfAmplitude} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2SelfAmplitude)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2TouchCount.toString() as any,
                    headerName: "2 Touch Count",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2TouchCount} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2TouchCount)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2TouchId.toString() as any,
                    headerName: "2 Touch Id",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2TouchId} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2TouchId)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Sigma2Uptime.toString() as any,
                    headerName: "2 Uptime",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Sigma2Uptime} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Sigma2Uptime)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Video',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.Video1RxState.toString() as any,
                    headerName: '1 RxState',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <VideoRXTXField id={Mri.WebApi.MeasurementId.Video1RxState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getVideoRXTXMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1RxState)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1TxState.toString() as any,
                    headerName: '1 TxState',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <VideoRXTXField id={Mri.WebApi.MeasurementId.Video1TxState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getVideoRXTXMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1TxState)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1InputState.toString() as any,
                    headerName: '1 Input State',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <VideoInputOutputField id={Mri.WebApi.MeasurementId.Video1InputState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getVideoInputOutputMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1InputState)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1InputXRes.toString() as any,
                    headerName: '1 Input XRes',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1InputXRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1InputXRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1InputYRes.toString() as any,
                    headerName: '1 Input YRes',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1InputYRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1InputYRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1InputFramesPerSec.toString() as any,
                    headerName: '1 Input FPS',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1InputFramesPerSec} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1InputFramesPerSec)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1InputPixelClock.toString() as any,
                    headerName: '1 Input Pixel Clock',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1InputPixelClock} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1InputPixelClock)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1InputFullRangeDetected.toString() as any,
                    headerName: '1 Input Full Range',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1InputFullRangeDetected} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1InputFullRangeDetected)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1OutputState.toString() as any,
                    headerName: '1 Output State',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <VideoInputOutputField id={Mri.WebApi.MeasurementId.Video1OutputState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getVideoInputOutputMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1OutputState)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1OutputXRes.toString() as any,
                    headerName: '1 Output XRes',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1OutputXRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1OutputXRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1OutputYRes.toString() as any,
                    headerName: '1 Output YRes',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1OutputYRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1OutputYRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1OutputFramesPerSec.toString() as any,
                    headerName: '1 Output FPS',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1OutputFramesPerSec} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1OutputFramesPerSec)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1OutputPixelClock.toString() as any,
                    headerName: '1 Output Pixel Clock',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1OutputPixelClock} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1OutputPixelClock)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video1OutputFullRangeDetected.toString() as any,
                    headerName: '1 Output Full Range',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video1OutputFullRangeDetected} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video1OutputFullRangeDetected)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video2InputRxState.toString() as any,
                    headerName: '2 Input RxState',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video2InputRxState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video2InputRxState)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video2InputState.toString() as any,
                    headerName: '2 Input State',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video2InputState} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video2InputState)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video2InputXRes.toString() as any,
                    headerName: '2 Input XRes',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video2InputXRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video2InputXRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video2InputYRes.toString() as any,
                    headerName: '2 Input YRes',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video2InputYRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video2InputYRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video2InputFullRangeDetected.toString() as any,
                    headerName: '2 Input Full Range',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video2InputFullRangeDetected} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video2InputFullRangeDetected)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.Video2OutputFullRangeDetected.toString() as any,
                    headerName: '2 Output Full Range',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.Video2OutputFullRangeDetected} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.Video2OutputFullRangeDetected)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.VideoRejuvenationModeActive.toString() as any,
                    headerName: 'Video Rejuve Active',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.VideoRejuvenationModeActive} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.VideoRejuvenationModeActive)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HdmiLinkQualityChan1.toString() as any,
                    headerName: 'HDMI Link 1',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HdmiLinkQualityChan1} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HdmiLinkQualityChan1)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HdmiLinkQualityChan2.toString() as any,
                    headerName: 'HDMI Link 2',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HdmiLinkQualityChan2} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HdmiLinkQualityChan2)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.HdmiLinkQualityChan3.toString() as any,
                    headerName: 'HDMI Link 3',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.HdmiLinkQualityChan3} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.HdmiLinkQualityChan3)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UrsaInputXRes.toString() as any,
                    headerName: 'Ursa X',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UrsaInputXRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UrsaInputXRes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.UrsaInputYRes.toString() as any,
                    headerName: 'Ursa Y',
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.UrsaInputYRes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.UrsaInputYRes)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'Accelerometer',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.MemsRoll.toString() as any,
                    headerName: "Roll",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MemsRoll} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MemsRoll)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.MemsPitch.toString() as any,
                    headerName: "Pitch",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MemsPitch} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MemsPitch)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.MemsShock.toString() as any,
                    headerName: "Shock",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MemsShock} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MemsShock)
                            : null,
                    initialHide: true,
                },
            ]
        },
        {
            headerName: 'CPU',
            children: [
                {
                    field: Mri.WebApi.MeasurementId.CPULoadOneMinuteAvgPct.toString() as any,
                    headerName: "1 Min %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CPULoadOneMinuteAvgPct} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CPULoadOneMinuteAvgPct)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CPULoadFiveMinuteAvgPct.toString() as any,
                    headerName: "5 Min %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CPULoadFiveMinuteAvgPct} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CPULoadFiveMinuteAvgPct)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.CPULoadTenMinuteAvgPct.toString() as any,
                    headerName: "10 Min %",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.CPULoadTenMinuteAvgPct} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.CPULoadTenMinuteAvgPct)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.MemoryTotalBytes.toString() as any,
                    headerName: "Total Bytes",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MemoryTotalBytes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MemoryTotalBytes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.MemoryFreeBytes.toString() as any,
                    headerName: "Free Bytes",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MemoryFreeBytes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MemoryFreeBytes)
                            : null,
                    initialHide: true,
                },
                {
                    field: Mri.WebApi.MeasurementId.MemoryUsedBytes.toString() as any,
                    headerName: "Used Bytes",
                    cellRenderer: (props: IDisplayFieldProps) => (
                        <MeasurementField id={Mri.WebApi.MeasurementId.MemoryUsedBytes} {...props} />
                    ),
                    valueGetter: (props) =>
                        props.data?.measurements
                            ? getMeasurementValue(props.data.measurements, Mri.WebApi.MeasurementId.MemoryUsedBytes)
                            : null,
                    initialHide: true,
                },
            ]
        },
    ];
}